import React, { useEffect } from "react";
import { Button, Space, Tour } from "antd";
import type { TourStepProps } from "antd";
import { useTour } from "./TourProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { InternalLink } from "../InternalLink";
import { useTranslation } from "react-i18next";
import { getTourForRoute } from "./utils/getTourForRoute";
import { getPathnameWithoutQuery } from "./utils/setupWalkthrough";
import "./tour.css";

const TOUR_SCROLL_INTO_VIEW = {
  block: "nearest" as const,
  inline: "nearest" as const,
};

/**
 * Helper function to click a tab by its key
 */
const clickTab = (tabKey: string): void => {
  const tabElement = document.querySelector(
    `[data-node-key="${tabKey}"]`
  ) as HTMLElement | null;
  if (tabElement) {
    tabElement.click();
  }
};

export const TourManager: React.FC = () => {
  const {
    open,
    current,
    steps,
    closeTour,
    hideTourTip,
    showTourTip,
    setCurrent,
    setPendingTour,
    clearPendingTour,
    setupWalkthroughActive,
    setupWalkthroughPhase,
    endSetupWalkthrough,
  } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Reset tour when route changes (pathname only, not query params)
  useEffect(() => {
    closeTour();
  }, [location.pathname, closeTour]);

  useEffect(() => {
    document.body.classList.toggle("app-tour-open", open);
    return () => {
      document.body.classList.remove("app-tour-open");
    };
  }, [open]);

  const handleChange = (currentStep: number) => {
    setCurrent(currentStep);
  };

  /** Finish / X / End tour — stop auto-resume so the guide stays off after navigation/reload. */
  const handleClose = () => {
    closeTour();
    clearPendingTour();
    endSetupWalkthrough();
  };

  const handleExitSetup = () => {
    closeTour();
    clearPendingTour();
    endSetupWalkthrough();
  };

  /** Re-open a tip that was only hidden, not finished. */
  const handleShowTip = () => {
    showTourTip();
  };

  const handleRecommendedTourClick = (path: string) => {
    closeTour();
    const pathname = getPathnameWithoutQuery(path);
    const hasTour =
      getTourForRoute(
        pathname,
        t,
        setupWalkthroughActive
          ? { setup: true, setupPhase: setupWalkthroughPhase }
          : undefined
      ) != null;
    if (hasTour) {
      setPendingTour({ path: pathname });
    }
    navigate(path);
  };

  const handleTabNavigation = (targetTab: string) => {
    closeTour();

    clickTab(targetTab);
    setPendingTour({ path: location.pathname, tab: targetTab });
    navigate(`${location.pathname}?tab=${targetTab}`, { replace: true });
  };

  const hideTipControl = setupWalkthroughActive ? (
    <div style={{ marginTop: "0.75rem" }}>
      <Button size="small" onClick={hideTourTip}>
        {t("Tour_Setup_HideTip")}
      </Button>
    </div>
  ) : null;

  // Only after "Hide tip" — finished/closed tours clear steps so this stays gone
  const showTipButtonVisible =
    setupWalkthroughActive && !open && steps.length > 0;

  return (
    <>
      <Tour
        open={open}
        current={current}
        onChange={handleChange}
        onClose={handleClose}
        animated={false}
        scrollIntoViewOptions={TOUR_SCROLL_INTO_VIEW}
        gap={{ offset: 6, radius: 4 }}
        steps={steps.map((step, index) => {
          const isLastStep = index === steps.length - 1;
          const stepRecommendedTours = (step.recommendedTours || []).filter(
            (tour) => {
              if (setupWalkthroughActive) {
                return true;
              }
              return (
                getTourForRoute(getPathnameWithoutQuery(tour.path), t) != null
              );
            }
          );
          const tabNavigation = step.tabNavigation;
          const baseDescription = step.description;

          const hasTabNavigation = isLastStep && tabNavigation;
          const hasRecommendedTours = stepRecommendedTours.length > 0;
          const needsSetupChrome =
            setupWalkthroughActive ||
            hasTabNavigation ||
            (isLastStep && hasRecommendedTours);

          let prevTab: string | null = null;
          let nextTab: string | null = null;

          if (tabNavigation) {
            const { currentTab, tabOrder } = tabNavigation;
            const currentTabIndex = tabOrder.indexOf(currentTab);
            if (currentTabIndex > 0) {
              prevTab = tabOrder[currentTabIndex - 1];
            }
            if (currentTabIndex < tabOrder.length - 1) {
              nextTab = tabOrder[currentTabIndex + 1];
            }
          }

          const description = needsSetupChrome ? (
            <div>
              {baseDescription}

              {hasTabNavigation && (
                <div
                  style={{
                    marginTop: "1rem",
                    paddingTop: "1rem",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <div style={{ marginBottom: "0.5rem", fontWeight: 500 }}>
                    {t("Tour_TabNavigation_Title")}:
                  </div>
                  <Space wrap>
                    {prevTab && (
                      <Button
                        size="small"
                        onClick={() => handleTabNavigation(prevTab)}
                      >
                        ← {t("Tour_Navigation_PreviousTab")}
                      </Button>
                    )}
                    {nextTab && (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => handleTabNavigation(nextTab)}
                      >
                        {t("Tour_Navigation_NextTab")} →
                      </Button>
                    )}
                    <Button size="small" danger onClick={handleClose}>
                      {t("Tour_Navigation_EndTour")}
                    </Button>
                    {setupWalkthroughActive && (
                      <Button size="small" onClick={handleExitSetup}>
                        {t("Tour_Setup_Exit")}
                      </Button>
                    )}
                  </Space>
                </div>
              )}

              {isLastStep && hasRecommendedTours && (
                <div
                  style={{
                    marginTop: "1rem",
                    paddingTop: "1rem",
                    borderTop: hasTabNavigation ? "none" : "1px solid #f0f0f0",
                  }}
                >
                  <div style={{ marginBottom: "0.5rem", fontWeight: 500 }}>
                    {setupWalkthroughActive
                      ? setupWalkthroughPhase === "complete"
                        ? t("Tour_Setup_AlsoExplore")
                        : t("Tour_Setup_NextStep")
                      : t("Recommended_Next_Tours")}
                    :
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    {stepRecommendedTours.map((tour, idx) => (
                      <InternalLink
                        key={idx}
                        to={tour.path}
                        onClick={() => handleRecommendedTourClick(tour.path)}
                        style={{
                          color: "#1890ff",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {tour.label}
                      </InternalLink>
                    ))}
                  </div>
                </div>
              )}

              {isLastStep &&
                setupWalkthroughActive &&
                !hasTabNavigation &&
                !hasRecommendedTours && (
                  <div
                    style={{
                      marginTop: "1rem",
                      paddingTop: "1rem",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <Space wrap>
                      <Button size="small" danger onClick={handleClose}>
                        {t("Tour_Navigation_EndTour")}
                      </Button>
                      <Button size="small" onClick={handleExitSetup}>
                        {t("Tour_Setup_Exit")}
                      </Button>
                    </Space>
                  </div>
                )}

              {isLastStep &&
                setupWalkthroughActive &&
                hasRecommendedTours &&
                !hasTabNavigation && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <Button size="small" onClick={handleExitSetup}>
                      {t("Tour_Setup_Exit")}
                    </Button>
                  </div>
                )}

              {hideTipControl}
            </div>
          ) : (
            baseDescription
          );

          return {
            ...step,
            description,
          } as TourStepProps;
        })}
      />

      {showTipButtonVisible && (
        <Button
          type="primary"
          onClick={handleShowTip}
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 1100,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {t("Tour_Setup_ShowTip")}
        </Button>
      )}
    </>
  );
};
